/**
 * Time-of-day — the TEMPORAL atmosphere axis, the counterpart to the spatial
 * cozy→spooky biome gradient (`presets.config.ts`). A single slow global clock
 * (night → dawn → day → dusk → …) modulates the scene over real time so the
 * world feels *alive*, not static — and, thematically, so the Coil's dread can
 * deepen after dusk on a second axis.
 *
 * Pure config + math here (TDD'd, no rendering/event deps). The runtime bridge
 * is `TimeOfDayDriver`, which advances this clock and feeds the EXISTING scene
 * seams — `EVENTS.SCENE_MOOD` today (the diurnal mood axis); the colorGrade
 * palette shift + per-layer modulator day-response ride the same clock next.
 * No new rendering substrate (per [[feedback_no_substrate_for_substrates_sake]]).
 *
 * @see ../../game/systems/atmosphere/TimeOfDayModel.ts — the stateful clock.
 * @see ../../game/systems/atmosphere/TimeOfDayDriver.ts — the runtime bridge.
 * @see docs/design/TIME_OF_DAY.md — design doc + the phase-2 seam map.
 */
import { presetForLevel } from './presets.config';

export type TimeOfDayPhase = 'night' | 'dawn' | 'day' | 'dusk';

export interface TimeOfDayConfig {
  /** Master switch — off → the driver never starts, the scene stays timeless. */
  enabled: boolean;
  /** Real seconds for ONE full night→dawn→day→dusk→night cycle. Slow on purpose. */
  cycleSeconds: number;
  /** Debug/QA multiplier on clock advance (1 = real time). The driver clamps it
   *  to 1 under reduce-motion so a QA fast-forward can never strobe the scene. */
  speedMultiplier: number;
  /** Clock phase the world boots at, 0..1 (0 = midnight, 0.5 = noon). */
  startPhase01: number;
}

export const TIME_OF_DAY: TimeOfDayConfig = {
  enabled: true,
  cycleSeconds: 20 * 60, // a gentle 20-minute day by default
  speedMultiplier: 1,
  startPhase01: 0.32, // boot at bright mid-morning — welcoming, not midnight
};

/** A time-keyed colorGrade palette — the PHASE-2 cinematic grade the clock
 *  pushes onto each preset's authored `colorGrade` layer (warm dawn → neutral
 *  noon → amber dusk → cool-dark night). The driver samples this each tick;
 *  AtmosphereLayer blends it with the authored grade (`TIME_GRADE_BLEND`). */
export interface TimeGrade {
  /** Ramp colour at the TOP of the viewport (0xRRGGBB). */
  top: number;
  /** Ramp colour at the BOTTOM of the viewport (0xRRGGBB). */
  bottom: number;
  /** Per-strip alpha (0..1). Higher = a stronger (darker at night) veil. */
  strength: number;
}

/** The interpolated atmosphere knobs at a clock phase (the driver consumes this). */
export interface TimeOfDayState {
  /** Raw clock phase, 0..1 (0 = midnight). */
  phase01: number;
  /** Named phase — HUD/debug + the seed for per-preset day-response tables. */
  phase: TimeOfDayPhase;
  /** Diurnal scene-mood multiplier (→ EVENTS.SCENE_MOOD). Gentle on purpose:
   *  a single UNIFORM scalar scales every mooded layer, so a big swing would
   *  mis-drive directional layers (it would thin fog at night, the opposite of
   *  the intent). Directional per-layer response (denser fog/bats at night,
   *  godRays only by day) is the phase-2 modulator work — see the design doc. */
  mood: number;
  /** Time-keyed colorGrade palette (phase 2). The driver emits this so
   *  AtmosphereLayer can blend the authored biome grade toward it — night reads
   *  cool-dark, noon reads near-authored (the biome's own look). */
  grade: TimeGrade;
}

/** How strongly the time-of-day grade shapes the authored biome grade each tick
 *  (0 = authored only, 1 = time grade replaces). 0.6 preserves biome identity
 *  while making night clearly darker + dawn/dusk clearly warmer. */
export const TIME_GRADE_BLEND = 0.6;

/** Wrap any real number into the unit clock [0,1). */
export function wrapPhase01(p: number): number {
  return ((p % 1) + 1) % 1;
}

/** Advance a phase by `dtMs` of real time under `cfg` (speed × cycle), wrapped.
 *  Ignores non-positive dt and a non-positive cycle (returns the wrapped input). */
export function advancePhase01(phase01: number, dtMs: number, cfg: TimeOfDayConfig = TIME_OF_DAY): number {
  if (!(dtMs > 0) || cfg.cycleSeconds <= 0) return wrapPhase01(phase01);
  const cycles = ((dtMs / 1000) * Math.max(0, cfg.speedMultiplier)) / cfg.cycleSeconds;
  return wrapPhase01(phase01 + cycles);
}

/** Diurnal mood keyframes on the cyclic clock (phase01 0 = midnight). Kept a mild
 *  swing for the reason in TimeOfDayState.mood. Evenly spaced but the sampler
 *  reads `at` generally, so the table can be re-shaped without touching it. */
interface MoodKey { at: number; mood: number; }
const MOOD_KEYS: MoodKey[] = [
  { at: 0.0, mood: 0.78 }, // midnight — the world is quietest/dimmest
  { at: 0.25, mood: 0.95 }, // dawn — waking up
  { at: 0.5, mood: 1.06 }, // noon — liveliest/brightest ambiance
  { at: 0.75, mood: 0.95 }, // dusk — settling down
];

/** Time-keyed colorGrade palette keyframes (phase 2). The sampler cosine-eases
 *  both the colours + strength around the cyclic clock so the grade crossfades
 *  slowly (no strobing — same easing as mood). At noon the grade is a near-no-op
 *  (white, low strength) so the authored biome grade reads through; at night it
 *  is a cool-dark veil (the only-darken path is the authored `blend: 'normal'`,
 *  which this strength feeds). Dawn/dusk are warm ambers. */
interface GradeKey { at: number; grade: TimeGrade; }
const GRADE_KEYS: GradeKey[] = [
  { at: 0.0, grade: { top: 0x0a1428, bottom: 0x0a1428, strength: 0.30 } }, // midnight — cool-dark veil
  { at: 0.25, grade: { top: 0xffd9a0, bottom: 0xf0c890, strength: 0.16 } }, // dawn — warm amber
  { at: 0.5, grade: { top: 0xffffff, bottom: 0xffffff, strength: 0.08 } }, // noon — near-no-op (authored shows)
  { at: 0.75, grade: { top: 0xffb060, bottom: 0xc87040, strength: 0.18 } }, // dusk — amber
];

/** The named phase for a clock position (bands chosen to feel right, not 1/4s). */
export function phaseNameFor(phase01: number): TimeOfDayPhase {
  const p = wrapPhase01(phase01);
  if (p < 0.2 || p >= 0.85) return 'night';
  if (p < 0.35) return 'dawn';
  if (p < 0.7) return 'day';
  return 'dusk';
}

/** Cosine-eased lerp (smooth S-curve) — keeps transitions gentle/non-strobing. */
function cosLerp(a: number, b: number, t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return a + (b - a) * (0.5 - 0.5 * Math.cos(c * Math.PI));
}

/** Linearly interpolate two 0xRRGGBB colours (each channel eased by `cosLerp`
 *  so the grade crossfades as smoothly as the mood). */
function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(cosLerp(ar, br, t));
  const g = Math.round(cosLerp(ag, bg, t));
  const bl = Math.round(cosLerp(ab, bb, t));
  return (r << 16) | (g << 8) | bl;
}

/** A generic cyclic keyframe bracket (last key wraps→first across the 1.0 seam). */
interface Bracket<T> { a: T; b: T; t: number; }
function bracketKeys<T extends { at: number }>(keys: T[], p: number): Bracket<T> {
  const n = keys.length;
  for (let i = 0; i < n; i += 1) {
    const a = keys[i];
    const b = keys[(i + 1) % n];
    const start = a.at;
    const end = b.at > a.at ? b.at : b.at + 1; // the wrap segment spans the 1.0 seam
    const pp = p >= start ? p : p + 1;
    if (pp >= start && pp < end) return { a, b, t: (pp - start) / (end - start) };
  }
  return { a: keys[n - 1], b: keys[0], t: 0 };
}

/** Sample the interpolated atmosphere state at a clock phase (cyclic, eased).
 *  Both the mood scalar AND the time-keyed colorGrade palette are eased around
 *  the cyclic clock so the whole scene breathes slowly with no strobing. */
export function sampleTimeOfDay(phase01: number): TimeOfDayState {
  const p = wrapPhase01(phase01);
  const mk = bracketKeys(MOOD_KEYS, p);
  const gk = bracketKeys(GRADE_KEYS, p);
  const ga = gk.a.grade, gb = gk.b.grade;
  const grade: TimeGrade = {
    top: lerpColor(ga.top, gb.top, gk.t),
    bottom: lerpColor(ga.bottom, gb.bottom, gk.t),
    strength: cosLerp(ga.strength, gb.strength, gk.t),
  };
  return { phase01: p, phase: phaseNameFor(p), mood: cosLerp(mk.a.mood, mk.b.mood, mk.t), grade };
}

/** The level's authored base scene-mood — the resting intensity the diurnal
 *  factor multiplies onto (so `final = base × diurnal × combat` composes). Defaults
 *  to 1 and ignores absent/invalid values, mirroring `resolveScene`'s `?? 1`. */
export function baseSceneMoodOf(level: { scene?: { sceneMood?: number } | null } | null | undefined): number {
  const m = level?.scene?.sceneMood;
  return typeof m === 'number' && m >= 0 ? m : 1;
}

// ── Per-scene opt-out (the seed of the "per-preset time-response map") ──
/** Enclosed, sky-less presets — they hold their own light, the day clock is N/A. */
export const INTERIOR_PRESETS = new Set<string>(['dungeon', 'cave', 'abyss']);
/** Authored clock-exempt maps by EXACT id — survives bespoke-preset swaps so
 *  interiors and the directed moonlit intro route keep their lighting
 *  instead of brightening under the outdoor sky clock. */
export const ALWAYS_NIGHT_MAPS = new Set<string>([
  'coil_undercroft',
  'tomb_waking',
  'village_arrival',
  'first_field',
  'moonfall_hollow',
  'tidewend_outskirts',
  'moonfen_pasture',
  'drowned_road',
  'drowned_road_hush_nook',
  'lanternwood',
  'lanternroot_hollow',
  'brackenmoor',
]);

/** Does this scene ignore the day/night sky clock? Interiors + authored
 *  clock-exempt maps do — the driver holds off SCENE_MOOD there so each
 *  preset's own enclosed lighting remains authoritative. */
export function isClockExemptScene(levelId: string): boolean {
  const id = levelId.toLowerCase();
  if (ALWAYS_NIGHT_MAPS.has(id)) return true;
  const preset = presetForLevel(levelId)?.id;
  return preset != null && INTERIOR_PRESETS.has(preset);
}
